import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { motion } from 'framer-motion';
import { 
  ShieldCheck, 
  Scale, 
  Briefcase, 
  Calculator, 
  Server,
  ArrowRight,
  CheckCircle2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: [0.25, 1, 0.5, 1] }
  }
};

const serviceIcons = {
  ehs: ShieldCheck,
  risk: Scale,
  pmo: Briefcase,
  payroll: Calculator,
  it: Server
};

const serviceColors = {
  ehs: { bg: 'bg-secondary/10', text: 'text-secondary', border: 'border-secondary' },
  risk: { bg: 'bg-primary/10', text: 'text-primary', border: 'border-primary' },
  pmo: { bg: 'bg-accent/20', text: 'text-accent-foreground', border: 'border-accent' },
  payroll: { bg: 'bg-secondary/10', text: 'text-secondary', border: 'border-secondary' },
  it: { bg: 'bg-primary/10', text: 'text-primary', border: 'border-primary' }
};

const serviceImages = {
  ehs: 'https://customer-assets.emergentagent.com/job_compliance-hub-324/artifacts/5vfcsfxk_EHS_Consultent.png',
  risk: 'https://images.unsplash.com/photo-1769740333462-9a63bfa914bc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1Mjh8MHwxfHNlYXJjaHwxfHxjb3Jwb3JhdGUlMjBib2FyZHJvb20lMjBtZWV0aW5nJTIwZGl2ZXJzZSUyMHByb2Zlc3Npb25hbHN8ZW58MHx8fHwxNzcwNjcwNzI2fDA&ixlib=rb-4.1.0&q=85',
  pmo: 'https://images.unsplash.com/photo-1768796372063-4da660e034b8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3MjQyMTd8MHwxfHNlYXJjaHw0fHxpbmR1c3RyaWFsJTIwc2FmZXR5JTIwbWFuYWdlbWVudCUyMHN5c3RlbSUyMHRlY2hub2xvZ3klMjBkYXNoYm9hcmR8ZW58MHx8fHwxNzcwNjgyNjExfDA&ixlib=rb-4.1.0&q=85',
  payroll: 'https://images.unsplash.com/photo-1762152212840-3ec91c031d52?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTV8MHwxfHNlYXJjaHw0fHxmaW5hbmNpYWwlMjBkb2N1bWVudHMlMjB0YXglMjBjYWxjdWxhdG9yJTIwYWNjb3VudGluZyUyMG9mZmljZXxlbnwwfHx8fDE3NzA2ODI2Mzh8MA&ixlib=rb-4.1.0&q=85',
  it: 'https://images.unsplash.com/photo-1763568258367-1c52beb60be7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2OTV8MHwxfHNlYXJjaHwxfHxzb2Z0d2FyZSUyMGRhc2hib2FyZCUyMGFuYWx5dGljcyUyMGNvbXB1dGVyJTIwc2NyZWVuJTIwbW9uaXRvcmluZ3xlbnwwfHx8fDE3NzA2ODI2NDN8MA&ixlib=rb-4.1.0&q=85'
};

export default function Services() {
  const { t, language } = useLanguage();
  const location = useLocation();

  useEffect(() => {
    // Scroll to section if hash present
    if (location.hash) {
      const element = document.getElementById(location.hash.slice(1));
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }
    } else {
      window.scrollTo(0, 0);
    }
  }, [location]);

  const services = ['ehs', 'risk', 'pmo', 'payroll', 'it'];

  return (
    <div className="min-h-screen" data-testid="services-page">
      {/* Hero Section */}
      <section className="relative py-24 bg-primary overflow-hidden" data-testid="services-hero">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `repeating-linear-gradient(
              45deg,
              transparent,
              transparent 20px,
              white 20px,
              white 21px
            )`
          }} />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-primary-foreground uppercase tracking-tight mb-6">
              {t.services.title}
            </h1>
            <p className="text-xl text-primary-foreground/80 max-w-3xl mx-auto">
              {t.services.subtitle}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-24 bg-background" data-testid="services-list">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="space-y-24"
          >
            {services.map((serviceKey, index) => {
              const Icon = serviceIcons[serviceKey];
              const colors = serviceColors[serviceKey];
              const serviceData = t.services[serviceKey];
              const isEven = index % 2 === 0;

              return (
                <motion.div
                  key={serviceKey}
                  id={serviceKey}
                  variants={itemVariants}
                  className="scroll-mt-32"
                  data-testid={`service-section-${serviceKey}`}
                >
                  <div className={`grid lg:grid-cols-2 gap-12 items-center ${!isEven ? 'lg:flex-row-reverse' : ''}`}>
                    {/* Content */}
                    <div className={isEven ? 'lg:order-1' : 'lg:order-2'}>
                      <div className={`inline-flex items-center gap-3 px-4 py-2 ${colors.bg} mb-6`}>
                        <Icon className={`w-6 h-6 ${colors.text}`} />
                        <span className={`font-semibold text-sm uppercase tracking-wider ${colors.text}`}>
                          {serviceData.shortTitle}
                        </span>
                      </div>

                      <h2 className="text-3xl md:text-4xl font-bold text-primary uppercase tracking-tight mb-6">
                        {serviceData.title}
                      </h2>

                      <p className="text-lg text-muted-foreground leading-relaxed mb-8">
                        {serviceData.description}
                      </p>

                      {/* Features */}
                      <div className="space-y-4 mb-8">
                        {serviceData.features.map((feature, idx) => (
                          <div 
                            key={idx}
                            className="flex items-start gap-3"
                          >
                            <CheckCircle2 className={`w-5 h-5 ${colors.text} flex-shrink-0 mt-0.5`} />
                            <span className="text-foreground">{feature}</span>
                          </div>
                        ))}
                      </div>

                      <Link to="/contact">
                        <Button 
                          className={`rounded-none px-6 py-5 font-semibold uppercase tracking-wide bg-primary text-primary-foreground hover:bg-primary/90`}
                        >
                          {language === 'en' ? 'Get Consultation' : 'احصل على استشارة'}
                          <ArrowRight className="ms-2 w-5 h-5" />
                        </Button>
                      </Link>
                    </div>

                    {/* Image */}
                    <div className={isEven ? 'lg:order-2' : 'lg:order-1'}>
                      <div className="relative">
                        <div className={`absolute -inset-4 ${colors.bg} blur-2xl opacity-50`} />
                        <img 
                          src={serviceImages[serviceKey]}
                          alt={serviceData.title}
                          className="relative w-full h-[400px] object-cover shadow-2xl"
                          style={{ 
                            clipPath: isEven 
                              ? 'polygon(0 0, 100% 0, 100% 90%, 90% 100%, 0 100%)'
                              : 'polygon(0 0, 100% 0, 100% 100%, 10% 100%, 0 90%)'
                          }}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Divider */}
                  {index < services.length - 1 && (
                    <div className="border-b border-border mt-24" />
                  )}
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-muted" data-testid="services-cta">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Card className="rounded-none border-2 border-primary overflow-hidden">
            <CardContent className="p-12 text-center">
              <h2 className="text-3xl md:text-4xl font-bold text-primary uppercase tracking-tight mb-6">
                {t.cta.title}
              </h2>
              <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
                {t.cta.subtitle}
              </p>
              <Link to="/contact">
                <Button 
                  size="lg"
                  className="bg-secondary text-secondary-foreground hover:bg-secondary/90 rounded-none px-10 py-6 font-semibold uppercase tracking-wide"
                >
                  {t.cta.button}
                  <ArrowRight className="ms-2 w-5 h-5" />
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
}
